import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ScreenMessage } from '../components/ui/ScreenMessage';
import { setApiBase, setAuthHooks } from '../lib/api';
import { MOCK_API } from '../lib/env';
import { loadConfig, type AppConfig } from './config';
import { clearSession, completeLogin, getValidIdToken, refreshSession, signOut as cognitoSignOut, startLogin } from './session';

type BootResult = { kind: 'ready'; config: AppConfig | null } | { kind: 'redirecting' } | { kind: 'error'; message: string; config: AppConfig | null };

const currentPath = (): string => `${window.location.pathname}${window.location.search}${window.location.hash}`;

let boot: Promise<BootResult> | null = null;

/** Runs once per page load (also under React StrictMode: an auth code can only be exchanged once). */
function bootstrap(): Promise<BootResult> {
  boot ??= (async (): Promise<BootResult> => {
    if (MOCK_API) {
      const { installMockApi } = await import('../mock/install');
      installMockApi();
      setAuthHooks({ getToken: async () => null, onUnauthenticated: () => undefined });
      return { kind: 'ready', config: null };
    }
    let config: AppConfig;
    try {
      config = await loadConfig();
    } catch (e) {
      return { kind: 'error', message: e instanceof Error ? e.message : 'Could not load configuration', config: null };
    }
    setApiBase(config.apiUrl);
    setAuthHooks({
      getToken: () => getValidIdToken(config),
      refreshToken: async () => (await refreshSession(config)) !== null,
      onUnauthenticated: () => {
        clearSession();
        void startLogin(config, currentPath());
      },
    });
    if (window.location.pathname === '/auth/callback') {
      try {
        const returnTo = await completeLogin(config, window.location.search);
        window.history.replaceState(null, '', returnTo);
        return { kind: 'ready', config };
      } catch (e) {
        return { kind: 'error', message: `Sign-in failed: ${e instanceof Error ? e.message : 'unknown error'}`, config };
      }
    }
    if (await getValidIdToken(config)) return { kind: 'ready', config };
    await startLogin(config, currentPath());
    return { kind: 'redirecting' };
  })();
  return boot;
}

interface AuthContextValue {
  signOut: () => void;
}
const AuthContext = createContext<AuthContextValue>({ signOut: () => undefined });
export const useAuth = (): AuthContextValue => useContext(AuthContext);

export function AuthGate({ children }: { children: ReactNode }) {
  const [result, setResult] = useState<BootResult | null>(null);
  useEffect(() => {
    let live = true;
    void bootstrap().then((r) => live && setResult(r));
    return () => {
      live = false;
    };
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      signOut: () => {
        if (result?.kind === 'ready' && result.config) cognitoSignOut(result.config);
        else window.location.assign('/signed-out');
      },
    }),
    [result],
  );

  if (!result) return <ScreenMessage title="Loading" busy />;
  if (result.kind === 'redirecting') return <ScreenMessage title="Redirecting to sign-in" detail="Taking you to your company login..." busy />;
  if (result.kind === 'error') {
    const cfg = result.config;
    return (
      <ScreenMessage
        title="Cannot sign in"
        detail={result.message}
        tone="error"
        action={cfg ? { label: 'Try again', onClick: () => void startLogin(cfg, '/') } : { label: 'Reload', onClick: () => window.location.reload() }}
      />
    );
  }
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function SignedOutPage() {
  const signIn = async () => {
    if (MOCK_API) {
      window.location.assign('/');
      return;
    }
    try {
      await startLogin(await loadConfig(), '/');
    } catch {
      window.location.assign('/');
    }
  };
  return <ScreenMessage title="You have been signed out" detail="Sign in again to continue to Mona Roadmap." action={{ label: 'Sign in', onClick: () => void signIn() }} />;
}
